import React, { useState } from 'react';
import {
  PhoneCall, FileText, CheckCircle, Clock, TrendingUp, Calendar, XCircle,
  Send, AlertCircle, Hash, MessageSquare, ChevronDown, Zap
} from 'lucide-react';
import IssueTemplateSelector from './IssueTemplateSelector';

const BASE = process.env.REACT_APP_API_URL || '';

const CALL_REASONS = [
  { value: 'claims_issue', label: 'Claims Issue', color: 'red' },
  { value: 'billing', label: 'Billing Question', color: 'orange' },
  { value: 'prior_auth', label: 'Prior Authorization', color: 'purple' },
  { value: 'enrollment_status', label: 'Enrollment Status', color: 'blue' },
  { value: 'id_card', label: 'ID Card Request', color: 'cyan' },
  { value: 'benefits_question', label: 'Benefits Question', color: 'teal' },
  { value: 'provider_search', label: 'Provider Search', color: 'green' },
  { value: 'rx_issue', label: 'Prescription Issue', color: 'pink' },
  { value: 'appeal', label: 'Appeal/Grievance', color: 'red' },
  { value: 'disenrollment', label: 'Disenrollment', color: 'slate' },
  { value: 'other', label: 'Other', color: 'gray' }
];

const OUTCOMES = [
  { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'emerald', desc: 'Issue resolved on this call' },
  { value: 'pending', label: 'Pending', icon: Clock, color: 'yellow', desc: 'Waiting on carrier action' },
  { value: 'escalated', label: 'Escalated', icon: TrendingUp, color: 'orange', desc: 'Escalated to supervisor/dept' },
  { value: 'callback_scheduled', label: 'Callback', icon: Calendar, color: 'blue', desc: 'Carrier will call back' },
  { value: 'no_answer', label: 'No Answer', icon: XCircle, color: 'red', desc: 'Could not reach carrier' },
];

export default function ActiveCasePanel({
  clientId, carrier, carriers = [], onEventCreated, onOutcomeLogged, timerData
}) {
  // Case creation state
  const [phase, setPhase] = useState('new'); // 'new' | 'active' | 'outcome' | 'done'
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(carrier || null);
  const [callReason, setCallReason] = useState('');
  const [callReasonDetail, setCallReasonDetail] = useState('');
  const [phoneDialed, setPhoneDialed] = useState('');
  const [liveNotes, setLiveNotes] = useState('');
  const [activeEvent, setActiveEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Outcome state
  const [outcome, setOutcome] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [requiresFollowUp, setRequiresFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpReason, setFollowUpReason] = useState('');

  const getCarrierPhone = (c) => {
    if (!c) return '';
    return c.ma_support_phone || c.medsup_support_phone || c.general_support_phone || '';
  };

  // Start a new case
  async function handleStartCase(e) {
    e.preventDefault();
    if (!callReason) { setError('Please select an issue type'); return; }

    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/service/clients/${clientId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: 'carrier_call',
          carrier_id: selectedCarrier?.id,
          carrier_name: selectedCarrier?.carrier_name || 'Unknown',
          phone_dialed: phoneDialed || getCarrierPhone(selectedCarrier),
          call_reason: callReason,
          call_reason_detail: callReasonDetail
        })
      });
      if (res.ok) {
        const data = await res.json();
        setActiveEvent(data.event);
        setPhase('active');
        if (onEventCreated) onEventCreated(data.event);
      } else {
        setError('Failed to create service event');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  }

  // Submit outcome
  async function handleSubmitOutcome(e) {
    e.preventDefault();
    if (!outcome) { setError('Please select an outcome'); return; }
    if (requiresFollowUp && !followUpDate) { setError('Follow-up date is required'); return; }

    setSubmitting(true);
    setError('');
    try {
      const body = {
        outcome,
        outcome_notes: [liveNotes, outcomeNotes].filter(Boolean).join('\n\n---\n\n'),
        reference_number: referenceNumber,
        requires_follow_up: requiresFollowUp,
        follow_up_date: requiresFollowUp ? followUpDate : null,
        follow_up_reason: requiresFollowUp ? followUpReason : null,
        call_duration_seconds: timerData?.callTime || 0,
        wait_time_seconds: timerData?.holdTime || 0,
      };

      const res = await fetch(`${BASE}/service/events/${activeEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const data = await res.json();
        setPhase('done');
        if (onOutcomeLogged) {
          onOutcomeLogged({
            event: data.event || activeEvent,
            outcome, outcomeNotes, referenceNumber, liveNotes,
            callReason, carrier: selectedCarrier,
            requiresFollowUp, followUpDate
          });
        }
      } else {
        setError('Failed to save outcome');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  }

  // Reset for new case
  function handleNewCase() {
    setPhase('new');
    setCallReason('');
    setCallReasonDetail('');
    setLiveNotes('');
    setActiveEvent(null);
    setOutcome('');
    setOutcomeNotes('');
    setReferenceNumber('');
    setRequiresFollowUp(false);
    setFollowUpDate('');
    setFollowUpReason('');
    setError('');
  }

  const reasonInfo = CALL_REASONS.find(r => r.value === callReason);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 border-b ${
        phase === 'active' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100' :
        phase === 'outcome' ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-100' :
        phase === 'done' ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-100' :
        'bg-gradient-to-r from-slate-50 to-white border-slate-100'
      }`}>
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <PhoneCall size={15} className={
            phase === 'active' ? 'text-blue-500' :
            phase === 'outcome' ? 'text-amber-500' :
            phase === 'done' ? 'text-emerald-500' :
            'text-slate-500'
          } />
          {phase === 'new' && 'Start New Service Case'}
          {phase === 'active' && 'Active Case'}
          {phase === 'outcome' && 'Log Outcome'}
          {phase === 'done' && 'Case Completed'}
        </h3>
      </div>

      <div className="p-4">
        {error && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* PHASE: NEW - Select issue */}
        {phase === 'new' && (
          <form onSubmit={handleStartCase} className="space-y-4">
            {/* Carrier selector (if no default) */}
            {!carrier && carriers.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Carrier</label>
                <select
                  value={selectedCarrier?.id || ''}
                  onChange={e => {
                    const c = carriers.find(c => c.id === e.target.value);
                    setSelectedCarrier(c);
                    if (c) setPhoneDialed(getCarrierPhone(c));
                  }}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none"
                >
                  <option value="">Select carrier...</option>
                  {carriers.map(c => (
                    <option key={c.id} value={c.id}>{c.carrier_name} ({c.carrier_code})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Quick Templates toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 transition-colors"
              >
                <Zap size={13} /> {showTemplates ? 'Hide Templates' : 'Use Template'}
              </button>
              {showTemplates && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <IssueTemplateSelector
                    onSelect={(t) => {
                      setCallReason(t.call_reason);
                      setCallReasonDetail(t.call_reason_detail || '');
                      setShowTemplates(false);
                    }}
                    onClose={() => setShowTemplates(false)}
                  />
                </div>
              )}
            </div>

            {/* Issue type grid */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">What's the issue?</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {CALL_REASONS.map(reason => (
                  <button
                    key={reason.value}
                    type="button"
                    onClick={() => setCallReason(reason.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all text-left ${
                      callReason === reason.value
                        ? `bg-${reason.color}-50 border-${reason.color}-300 text-${reason.color}-800 ring-1 ring-${reason.color}-200`
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Details</label>
              <textarea
                value={callReasonDetail}
                onChange={e => setCallReasonDetail(e.target.value)}
                placeholder="Describe the client's issue..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={!callReason || submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <PhoneCall size={15} /> {submitting ? 'Starting...' : 'Start Case'}
            </button>
          </form>
        )}

        {/* PHASE: ACTIVE - Live notes */}
        {phase === 'active' && (
          <div className="space-y-4">
            {/* Case summary bar */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-1 rounded text-xs font-semibold bg-${reasonInfo?.color || 'slate'}-100 text-${reasonInfo?.color || 'slate'}-800`}>
                {reasonInfo?.label || callReason}
              </span>
              {selectedCarrier && (
                <span className="text-xs text-slate-500">{selectedCarrier.carrier_name}</span>
              )}
              {(phoneDialed || getCarrierPhone(selectedCarrier)) && (
                <button
                  onClick={async () => {
                    const num = (phoneDialed || getCarrierPhone(selectedCarrier)).replace(/[^0-9+]/g, '');
                    try {
                      const BASE = process.env.REACT_APP_API_URL || '';
                      const res = await fetch(`${BASE}/calls/initiate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ clientPhone: num }),
                      });
                      if (!res.ok) {
                        setError('Failed to initiate call. Please try again.');
                      }
                    } catch (err) {
                      console.error('Call initiation error:', err);
                      setError('Failed to initiate call. Please try again.');
                    }
                  }}
                  className="text-xs text-indigo-600 font-mono hover:underline cursor-pointer"
                >
                  {phoneDialed || getCarrierPhone(selectedCarrier)}
                </button>
              )}
            </div>

            {callReasonDetail && (
              <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                {callReasonDetail}
              </p>
            )}

            {/* Live notes scratchpad */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                <FileText size={13} /> Live Notes
                <span className="font-normal text-slate-400">— take notes as you work</span>
              </label>
              <textarea
                value={liveNotes}
                onChange={e => setLiveNotes(e.target.value)}
                placeholder="Rep name: &#10;Hold time: &#10;What they said: &#10;Resolution: ..."
                rows={8}
                className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none resize-y font-mono leading-relaxed"
                autoFocus
              />
            </div>

            <button
              onClick={() => setPhase('outcome')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <CheckCircle size={15} /> Log Outcome
            </button>
          </div>
        )}

        {/* PHASE: OUTCOME - Select result */}
        {phase === 'outcome' && (
          <form onSubmit={handleSubmitOutcome} className="space-y-4">
            {/* Outcome selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">What happened?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {OUTCOMES.map(o => {
                  const Icon = o.icon;
                  const selected = outcome === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        setOutcome(o.value);
                        if (o.value === 'callback_scheduled') setRequiresFollowUp(true);
                      }}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                        selected
                          ? `bg-${o.color}-50 border-${o.color}-300 ring-1 ring-${o.color}-200`
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon size={18} className={selected ? `text-${o.color}-600` : 'text-slate-400'} />
                      <div>
                        <div className={`text-sm font-semibold ${selected ? `text-${o.color}-800` : 'text-slate-700'}`}>{o.label}</div>
                        <div className="text-[11px] text-slate-500">{o.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Reference number */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                <Hash size={12} /> Reference / Case Number
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                placeholder="Carrier reference number..."
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none font-mono"
              />
            </div>

            {/* Outcome notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
                <MessageSquare size={12} /> Resolution Notes
              </label>
              <textarea
                value={outcomeNotes}
                onChange={e => setOutcomeNotes(e.target.value)}
                placeholder="What was the resolution? Any next steps?"
                rows={3}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 focus:border-indigo-300 outline-none resize-none"
              />
            </div>

            {/* Follow-up */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresFollowUp}
                  onChange={e => setRequiresFollowUp(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs font-semibold text-slate-700">Requires follow-up</span>
              </label>

              {requiresFollowUp && (
                <div className="mt-3 space-y-2">
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={e => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 outline-none"
                  />
                  <input
                    type="text"
                    value={followUpReason}
                    onChange={e => setFollowUpReason(e.target.value)}
                    placeholder="Follow-up reason..."
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-300 outline-none"
                  />
                  <p className="text-[10px] text-indigo-600">A task will be created automatically for this follow-up.</p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPhase('active')}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Back to Notes
              </button>
              <button
                type="submit"
                disabled={!outcome || submitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Send size={14} /> {submitting ? 'Saving...' : 'Save Outcome'}
              </button>
            </div>
          </form>
        )}

        {/* PHASE: DONE */}
        {phase === 'done' && (
          <div className="text-center py-6">
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
            <h4 className="text-base font-bold text-slate-800 mb-1">Case Logged</h4>
            <p className="text-xs text-slate-500 mb-4">
              {reasonInfo?.label} — {OUTCOMES.find(o => o.value === outcome)?.label}
              {referenceNumber && ` — Ref #${referenceNumber}`}
            </p>
            <button
              onClick={handleNewCase}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Start New Case
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
